import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Wand2, Gamepad2, Globe, Search, Share2, Play, Trash2,
  Loader2, Plus, Package, Sparkles, ExternalLink, Copy,
} from "lucide-react";import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import LiveBadge from "../components/LiveBadge";

const KIND_META = {
  app: { label: "App", icon: Wand2, color: "bg-saffron/15 text-marigold border-saffron/40", newPath: "/studio" },
  game: { label: "Game", icon: Gamepad2, color: "bg-teal/15 text-teal border-teal/40", newPath: "/games" },
  website: { label: "Website", icon: Globe, color: "bg-marigold/15 text-marigold border-marigold/40", newPath: "/website-studio" },
};

export default function Projects() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ apps: 0, games: 0, websites: 0, total: 0 });
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    load();
    // eslint-disable-next-line
  }, [user]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects/mine");
      setItems(data.items || []);
      setCounts(data.counts || counts);
    } catch { toast.error("Could not load your projects."); }
    finally { setLoading(false); }
  };

  const visible = useMemo(() => {
    let list = items;
    if (filter !== "all") list = list.filter((i) => i.kind === filter.replace(/s$/, ""));
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((i) => (i.title || "").toLowerCase().includes(q) || (i.prompt || "").toLowerCase().includes(q));
    }
    return list;
  }, [items, filter, query]);

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This can't be undone.`)) return;
    setDeleting(item.id);
    const paths = {
      app: `/prompt/blueprints/${item.id}`,
      game: `/games/mine/${item.id}`,
      website: `/website/projects/${item.id}`,
    };
    try {
      await api.delete(paths[item.kind]);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      setCounts((prev) => {
        const key = item.kind === "app" ? "apps" : item.kind === "game" ? "games" : "websites";
        return { ...prev, [key]: Math.max(0, (prev[key] || 0) - 1), total: Math.max(0, (prev.total || 0) - 1) };
      });
      toast.success("Deleted");
    } catch { toast.error("Could not delete."); }
    finally { setDeleting(null); }
  };

  const copyShareLink = async (item) => {
    const url = `${window.location.origin}${item.share_path}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied");
    } catch { toast.error("Copy failed"); }
  };

  if (!user) {
    return (
      <div className="container-narrow py-24 text-center" data-testid="projects-anon">
        <Package className="w-10 h-10 text-marigold mx-auto mb-4" />
        <h1 className="font-heading text-4xl font-semibold mb-3">Your workshop is one login away.</h1>
        <p className="text-ink2 max-w-md mx-auto mb-6">
          Log in to see every app, game and website you&apos;ve built with Durga — all on one page.
        </p>
        <div className="flex gap-2 justify-center">
          <button onClick={() => nav("/login")} className="btn-primary" data-testid="projects-login">Log in</button>
          <button onClick={() => nav("/signup")} className="btn-outline-ink" data-testid="projects-signup">Start free</button>
        </div>
      </div>
    );
  }

  return (
    <div className="container-narrow py-10 md:py-14" data-testid="projects-page">
      {/* Header */}
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow mb-2">Your workshop</div>
          <h1 className="font-heading text-4xl md:text-5xl font-semibold">My Projects</h1>
          <p className="text-ink2 mt-2">
            Every app, game and website you&apos;ve made. Resume, share, or delete.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => nav("/studio")} className="btn-outline-ink text-sm" data-testid="projects-new-app">
            <Wand2 className="w-4 h-4" /> New app
          </button>
          <button onClick={() => nav("/games")} className="btn-outline-ink text-sm" data-testid="projects-new-game">
            <Gamepad2 className="w-4 h-4" /> New game
          </button>
          <button onClick={() => nav("/website-studio")} className="btn-primary text-sm" data-testid="projects-new-website">
            <Plus className="w-4 h-4" /> New website
          </button>
        </div>
      </div>

      {/* Filter + search */}
      <div className="flex flex-wrap gap-3 items-center mb-6" data-testid="projects-filters">
        <div className="flex gap-1 bg-paper p-1 rounded-full border border-border">
          <FilterTab active={filter === "all"} onClick={() => setFilter("all")} label={`All · ${counts.total}`} tid="filter-all" />
          <FilterTab active={filter === "apps"} onClick={() => setFilter("apps")} label={`Apps · ${counts.apps}`} tid="filter-apps" />
          <FilterTab active={filter === "games"} onClick={() => setFilter("games")} label={`Games · ${counts.games}`} tid="filter-games" />
          <FilterTab active={filter === "websites"} onClick={() => setFilter("websites")} label={`Websites · ${counts.websites}`} tid="filter-websites" />
        </div>
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink2" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by title or prompt…"
            className="w-full bg-paper border border-border rounded-full pl-9 pr-4 py-2 text-sm outline-none focus:border-ink"
            data-testid="projects-search"
          />
        </div>
      </div>

      {loading && (
        <div className="text-center py-16" data-testid="projects-loading">
          <Loader2 className="w-6 h-6 animate-spin text-marigold mx-auto mb-2" />
          <div className="text-sm text-ink2">Loading your projects…</div>
        </div>
      )}

      {!loading && visible.length === 0 && (
        <EmptyState hasAny={items.length > 0} query={query} onReset={() => { setFilter("all"); setQuery(""); }} onCreate={() => nav("/website-studio")} />
      )}

      {!loading && visible.length > 0 && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-grid">
          {visible.map((item) => (
            <ProjectCard
              key={`${item.kind}-${item.id}`}
              item={item}
              onResume={() => nav(item.resume_path)}
              onShare={() => copyShareLink(item)}
              onDelete={() => remove(item)}
              deleting={deleting === item.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FilterTab({ active, onClick, label, tid }) {
  return (
    <button
      onClick={onClick}
      className={`text-xs px-4 py-1.5 rounded-full transition ${active ? "bg-ink text-cream" : "text-ink2 hover:text-ink"}`}
      data-testid={`projects-${tid}`}
    >
      {label}
    </button>
  );
}

function ProjectCard({ item, onResume, onShare, onDelete, deleting }) {
  const meta = KIND_META[item.kind] || KIND_META.website;
  const KindIcon = meta.icon;
  const palette = item.palette || {};
  const preview = item.kind === "website" && palette.primary
    ? `linear-gradient(135deg, ${palette.cream || "#F9F6F0"} 0%, ${palette.muted || palette.accent || "#E9B979"} 55%, ${palette.primary || "#1F1A17"} 100%)`
    : item.kind === "app"
      ? "linear-gradient(135deg,#FFF3D6,#FBD979 55%,#F0A324)"
      : "linear-gradient(135deg,#D6F1EA,#6FCDBB 55%,#1B4B33)";

  const dateStr = item.created_at
    ? new Date(item.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : "";

  const [hoverHtml, setHoverHtml] = useState(null); // preview_html lazy-loaded for websites
  const [hovering, setHovering] = useState(false);

  const onEnter = async () => {
    setHovering(true);
    if (item.kind !== "website" || hoverHtml) return;
    try {
      const { data } = await api.get(`/website/projects/${item.id}`);
      if (data?.preview_html) setHoverHtml(data.preview_html);
    } catch { /* silent — hover thumbnail is a nice-to-have */ }
  };

  return (
    <div
      className="card-soft overflow-hidden group hover:border-ink transition"
      data-testid={`project-card-${item.id}`}
      onMouseEnter={onEnter}
      onMouseLeave={() => setHovering(false)}
    >
      <button
        onClick={onResume}
        className="block w-full h-32 relative overflow-hidden text-left"
        style={{ background: preview }}
        data-testid={`project-resume-${item.id}`}
      >
        {/* Live thumbnail on hover (websites only, once loaded) */}
        {item.kind === "website" && hoverHtml && (
          <div
            className={`absolute inset-0 transition-opacity duration-300 ${hovering ? "opacity-100" : "opacity-0"}`}
            aria-hidden="true"
            data-testid={`project-thumb-${item.id}`}
          >
            <div
              className="absolute pointer-events-none"
              style={{
                width: "1440px",
                height: "900px",
                transform: "scale(0.2)",
                transformOrigin: "top left",
                left: "0",
                top: "0",
              }}
            >
              <iframe
                title={`${item.title} live thumbnail`}
                srcDoc={hoverHtml}
                sandbox="allow-scripts"
                className="w-full h-full bg-white border-0"
              />
            </div>
          </div>
        )}
        {item.kind === "website" && hovering && !hoverHtml && (
          <div className="absolute inset-0 flex items-center justify-center text-cream/80 text-[10px] uppercase tracking-wider font-semibold pointer-events-none">
            <Loader2 className="w-4 h-4 animate-spin mr-1" /> Loading preview…
          </div>
        )}
        <div className="absolute inset-0 flex items-end p-3 pointer-events-none">
          <div className="w-8 h-8 rounded-lg bg-cream/95 flex items-center justify-center shadow-sm">
            <KindIcon className="w-4 h-4 text-ink" />
          </div>
        </div>
        <div className="absolute top-2 right-2 pointer-events-none">
          <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${meta.color} bg-cream/90`}>
            {meta.label}
          </span>
        </div>
      </button>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="font-heading font-semibold text-ink truncate flex-1" title={item.title}>{item.title}</h3>
          {item.kind === "website" && <LiveBadge blueprintId={item.id} saved variant="dot" />}
          {item.is_public && (
            <span className="text-[10px] text-teal font-semibold uppercase tracking-wider" title="Publicly shared">public</span>
          )}
        </div>
        {item.prompt && (
          <p className="text-xs text-ink2 line-clamp-2 mb-2 leading-relaxed">{item.prompt}</p>
        )}
        <div className="text-[10px] uppercase tracking-wider text-ink2/70 mb-3">
          {dateStr}{item.meta ? ` · ${item.meta}` : ""}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={onResume}
            className="btn-primary text-xs flex-1 justify-center"
            data-testid={`project-resume-btn-${item.id}`}
          >
            <Play className="w-3 h-3" /> Resume
          </button>
          <button
            onClick={onShare}
            className="btn-outline-ink text-xs px-3"
            title="Copy share link"
            data-testid={`project-share-${item.id}`}
          >
            <Share2 className="w-3 h-3" />
          </button>
          <button
            onClick={onDelete}
            disabled={deleting}
            className="text-xs px-3 py-1.5 rounded-full text-ink2 hover:text-marigold hover:bg-marigold/10 disabled:opacity-40"
            title="Delete"
            data-testid={`project-delete-${item.id}`}
          >
            {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ hasAny, query, onReset, onCreate }) {
  if (hasAny) {
    return (
      <div className="text-center py-16" data-testid="projects-empty-filter">
        <Search className="w-10 h-10 text-ink2 mx-auto mb-3" />
        <h3 className="font-heading text-xl font-semibold mb-1">No projects match &ldquo;{query}&rdquo;</h3>
        <button onClick={onReset} className="btn-outline-ink text-sm mt-2" data-testid="projects-empty-reset">
          Clear filters
        </button>
      </div>
    );
  }
  return (
    <div className="text-center py-20 card-soft" data-testid="projects-empty">
      <Sparkles className="w-10 h-10 text-marigold mx-auto mb-3" />
      <h3 className="font-heading text-2xl font-semibold mb-2">Your workshop is quiet.</h3>
      <p className="text-ink2 max-w-md mx-auto mb-5">
        Start with a website, an Apple app or a game — every one you make lands right here.
      </p>
      <button onClick={onCreate} className="btn-primary" data-testid="projects-empty-cta">
        <Plus className="w-4 h-4" /> Build my first website
      </button>
    </div>
  );
}
